// Grids & Gantts — a view that moves SHOWS the reader it moved.
//
// Standard 1.4: WHEN A PAGE TAKES THE READER SOMEWHERE, IT TRAVELS.
//
// A chart three thousand pixels wide that teleports leaves the reader asking
// which way they just went, and whether anything moved at all. The travel is
// not decoration: it is the answer to "where did that come from".
//
// WHY THIS IS NOT `scrollTo({ behavior: 'smooth' })`. That call is a REQUEST,
// and where it is not honoured NOTHING HAPPENS — not an instant jump, no
// movement at all. Measured in this app's own browser pane: the smooth call
// left scrollLeft exactly where it was, so a "Scroll to task" button lit up,
// said the right thing and did nothing. `scroll-behavior: smooth` in CSS
// fails identically, because it turns a plain assignment into the same
// ignored animation. So the animation is run by hand, and the maths lives
// here — pure, shared, and tested under node.
//
// Three rules it keeps:
//
//   it is quick        a nudge takes 220ms; a jump across a year takes 520,
//                      never more — past about half a second an animation
//                      stops being information and becomes a wait
//   it eases           constant speed reads as a machine dragging the page;
//                      eased in and out reads as the page travelling
//   it always arrives   see `LAND_GRACE` — the frames can simply stop

// How long a move of `distance` pixels should take.
export const MIN_MS = 220;
export const MAX_MS = 520;
export const glideMs = (distance) => Math.round(Math.max(MIN_MS, Math.min(MAX_MS, MIN_MS + Math.abs(Number(distance) || 0) * 0.22)));

// Ease in-out cubic: slow at both ends, quickest in the middle.
export const easeInOut = (t) => {
  const x = Math.max(0, Math.min(1, Number(t) || 0));
  return x < 0.5 ? 4 * x * x * x : 1 - ((-2 * x + 2) ** 3) / 2;
};

// Where the view is, `elapsed` into a move from `from` to `to` over `ms`.
// Past the end it is exactly `to` — an animation that stops a pixel short
// leaves the thing it was fetching one pixel off the edge.
export function glidePosition(from, to, elapsed, ms) {
  const a = Number(from) || 0;
  const b = Number(to) || 0;
  const d = Number(ms) || 0;
  if (d <= 0 || elapsed >= d) return b;
  return a + (b - a) * easeInOut((Number(elapsed) || 0) / d);
}

// A move shorter than this is not worth animating — the eye cannot read two
// pixels of travel, and a frame of easing over it just looks like a flicker.
export const MIN_TRAVEL = 2;

// How long after the animation should have ended to give up and land anyway.
//
// requestAnimationFrame is throttled to NOTHING in a tab that is not painting
// — a background tab, a hidden pane, a minimised window — so a glide can be
// started and never get a single frame. A timer lands it regardless. This is
// not belt and braces: it is the difference between "the button works" and
// "the button works when you are looking at it".
export const LAND_GRACE = 260;

// Whether to travel at all, or simply be there. A reader who has asked their
// system for less motion is not asking for a slower animation, they are
// asking for none — the honest answer is to arrive.
export function shouldGlide(distance, { reduceMotion = false, animate = true } = {}) {
  if (!animate || reduceMotion) return false;
  return Math.abs(Number(distance) || 0) >= MIN_TRAVEL;
}

export const prefersReducedMotion = () => (
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
);
